import { useEffect, useState } from "react";
import { getGender, getClientType } from "../services/valuesService";
import FormButton from "./FormActionButtons";
import {
  Trash2,
  Save,
  Loader2,
  ArrowLeft,
  ChevronsRight,
  Plus,
  SendHorizonal,
} from "lucide-react";
import {
  getCompanyClient,
  saveCompanyClient,
  updateCompanyClient,
  submitCompanyForm,
  updateCompanyFormStatus,
  getCompanyDetailsById,
} from "../services/companyService";
import { useNavigate } from "react-router-dom";
import { useModal } from "../context/ModalContext";
import Loading from "./Loading";
import ManagerActionButtons from "./ManagerActionButtons";
import { useToast } from "../context/ToastContext";
import isEqual from "lodash/isEqual";
import { useAuth } from "../context/AuthContext.js";
import UserRoles from "../constants/userRoles.js";
import FormNavigationButtons from "./FormNavigationButtons.js";

function ClientInformationForm({
  companyId,
  versionId = 1,
  onNext,
  onBack,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [basicInfo, setBasicInfo] = useState({});
  const navigate = useNavigate();
  const { showModal } = useModal();
  const toast = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    companyId: companyId,
    version: versionId,
    clientType: 1,
    clientName: "",
    gender: 1,
    address: {
      registeredAddress: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      addressType: 1,
    },
    contacts: [
      { value: "", contactType: 2 }, // Official Contact Number
      { value: "", contactType: 3 }, // Telephone/Fax
      { value: "", contactType: 4 }, // Email
    ],
    designation: "",
    socialLinks: [""],
    hasCriminalHistory: false,
    criminalHistoryDescription: "",
  });

  const [genders, setGenders] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [company, setCompany] = useState([]);

  const fetchValues = async () => {
    const genderData = await getGender();
    const clientTypeData = await getClientType();
    setGenders(genderData);
    setClientTypes(clientTypeData);
  };

  const fetchBasicInfo = async () => {
    try {
      setLoading(true);
      let fetchVersion = versionId;
      let isFallback = false;
      let response = await getCompanyClient(companyId, versionId);

      if ((!response || response.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        response = await getCompanyClient(companyId, fetchVersion);
        setFallbackUsed(true);
      }

      if (response) {
        const clientData = response[0]; // or however you receive it
        const mappedData = mapClientResponseToForm(clientData);
        //console.log(mappedData);
        setFormData(mappedData);
        setBasicInfo(mappedData);
        setIsChanged(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch basic info:", error);
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    if (isEditable) {
      const response = await getCompanyDetailsById(companyId);
      if (response && response.id) {
        setCompany(response);
      }
    }
  };

  useEffect(() => {
    fetchValues();
    fetchBasicInfo();
    fetchCompanyDetails();
  }, []);

  const handleChange = (field, value) => {
    setIsChanged(true);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setIsChanged(true);
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleContactChange = (id, value) => {
    const updatedContacts = formData.contacts.map((contact) => {
      if (contact.contactType === id) {
        return { ...contact, value };
      }
      return contact;
    });

    setFormData((prev) => ({ ...prev, contacts: updatedContacts }));
    setIsChanged(true);
  };

  const handleSocialLinkChange = (index, value) => {
    setIsChanged(true);
    const updatedLinks = [...formData.socialLinks];
    updatedLinks[index] = value;
    setFormData((prev) => ({ ...prev, socialLinks: updatedLinks }));
  };

  const addSocialLink = () => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, ""],
    }));
  };

  const removeSocialLink = (index) => {
    setFormData((prev) => {
      const updatedLinks = [...prev.socialLinks];
      updatedLinks.splice(index, 1);
      return { ...prev, socialLinks: updatedLinks };
    });
    setIsChanged(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let response;

      if (formData.id && formData.id !== 0 && !fallbackUsed) {
        // It's an update
        response = await updateCompanyClient(formData);
      } else {
        // It's a new create
        response = await saveCompanyClient(formData);
      }

      if (response.status) {
        // setIsChanged(false);
        toast.success("Company Information saved successfully");
        // Optionally refresh or show success message
      }
    } catch (error) {
      console.error("Save or update failed:", error);
      toast.success("Company Information failed to save");
    } finally {
      setSaving(false);
    }
  };
  const handleSubmit = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to submit the form?"
    );
    if (!confirmed) return;

    await handleSave();
    const response = await submitCompanyForm(companyId, versionId);
    if (response.status) {
      // showModal("Submitted Successfully");
      toast.success("Submitted Successfully");
      navigate("/");
    } else {
      // showModal("Submit Failed", response.message);
      toast.success("Submit Failed");
    }
  };

  function mapClientResponseToForm(client) {
    return {
      id: client.id,
      companyId: companyId,
      version: versionId,
      clientType: client.clientType?.id ?? null,
      clientName: client.name,
      gender: client.gender?.id ?? null,
      designation: client.designation,
      address: {
        ...client.address,
        addressType: client.address?.addressType?.id ?? null,
      },
      contacts: client.contacts.map((c) => ({
        id: c.id,
        value: c.value,
        contactType: c.contactType,
      })),
      socialLinks: client.socialLinks.map((link) => link.url),
      hasCriminalHistory: client.hasCriminalHistory,
      criminalHistoryDescription: client.criminalHistoryDescription,
    };
  }

  const handleApprove = async () => {
    console.log("Form Approved!");
    const response = await handleUpdateManagerFormStatus(6);
    if (response.status) {
      navigate("/submissions");
    }
  };

  const handleReject = async (remarks) => {
    // Update UI or perform other actions after rejection with remarks
    console.log("Form Rejected with remarks:", remarks);
    const response = await handleUpdateManagerFormStatus(7, remarks);
    if (response.status) {
      navigate("/submissions");
    }
  };

  const handleResent = async (remarks) => {
    // Update UI or perform other actions after resending with remarks
    console.log("Form Resent with remarks:", remarks);
    const response = await handleUpdateManagerFormStatus(8, remarks);
    if (response.status) {
      navigate("/submissions");
    }
  };

  const handleUpdateManagerFormStatus = async (status, remark = "") => {
    const response = await updateCompanyFormStatus(
      companyId,
      versionId,
      status,
      remark
    );
    return response;
  };

  const hasChanges = isEqual(basicInfo?.socialLinks, formData?.socialLinks);

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="px-10 py-6 bg-white">
      <h2 className="text-lg font-semibold mb-6">Client Information</h2>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <input
          placeholder="Client Name"
          value={formData.clientName}
          onChange={(e) => handleChange("clientName", e.target.value)}
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          disabled={!isEditable}
        />
        <select
          value={formData.clientType}
          onChange={(e) => handleChange("clientType", parseInt(e.target.value))}
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          disabled={!isEditable}
        >
          <option value="">Select Client Type</option>
          {clientTypes.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.value}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-4">
          Gender:
          {genders.map((g) => (
            <label key={g.id} className="flex items-center gap-1">
              <input
                type="radio"
                name="gender"
                value={g.id}
                checked={formData.gender === g.id}
                onChange={() => handleChange("gender", g.id)}
                disabled={!isEditable}
              />
              {g.value}
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <input
          placeholder="Contact Number"
          value={formData.contacts.find((x) => x.contactType === 2)?.value}
          onChange={(e) => handleContactChange(2, e.target.value)}
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          type="tel"
          disabled={!isEditable}
        />
        <input
          placeholder="Telephone/Fax"
          value={formData.contacts.find((x) => x.contactType === 3)?.value}
          onChange={(e) => handleContactChange(3, e.target.value)}
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          type="tel"
          disabled={!isEditable}
        />
        <input
          placeholder="Email"
          value={formData.contacts.find((x) => x.contactType === 4)?.value}
          onChange={(e) => handleContactChange(4, e.target.value)}
          type="email"
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          disabled={!isEditable}
        />
        <input
          placeholder="Designation"
          value={formData.designation}
          onChange={(e) => handleChange("designation", e.target.value)}
          className={`px-4 py-2 border rounded ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          disabled={!isEditable}
        />
      </div>
      <div className="mb-4">
        <label className="block mb-1">Social Links</label>
        {formData.socialLinks.map((link, idx) => (
          <div key={idx} className="flex items-center gap-2 mb-2">
            <input
              value={link}
              placeholder={`Social Link ${idx + 1}`}
              onChange={(e) => handleSocialLinkChange(idx, e.target.value)}
              className={`w-full px-4 py-2 border rounded ${
                !isEditable ? "bg-gray-100" : ""
              }`}
              type="url"
              disabled={!isEditable}
            />
            {!isEditable ||
              (formData.socialLinks.length > 1 && (
                <button
                  onClick={() => removeSocialLink(idx)}
                  className="text-red-500 text-sm cursor-pointer"
                  type="button"
                >
                  <Trash2 />
                </button>
              ))}
          </div>
        ))}
        {isEditable && (
          <FormButton
            icon={Plus}
            onClick={addSocialLink}
            variant="secondary"
            additionalClass="mt-4"
          >
            Add More
          </FormButton>
        )}
      </div>
      <div className="mb-4 hidden">
        <label className="block mb-1">Social Links</label>
        {formData.socialLinks.map((link, idx) => (
          <input
            key={idx}
            value={link}
            placeholder={`Social Link ${idx + 1}`}
            onChange={(e) => handleSocialLinkChange(idx, e.target.value)}
            className="w-full px-4 py-2 border rounded mb-2"
            type="url"
          />
        ))}
        <FormButton
          icon={Plus}
          onClick={addSocialLink}
          variant="secondary"
          additionalClass="mt-4"
        >
          Add More
        </FormButton>
      </div>

      <div className="mb-6">
        <label className="block mb-2">Registered Address</label>
        <input
          placeholder="Address"
          value={formData.address.registeredAddress}
          onChange={(e) =>
            handleAddressChange("registeredAddress", e.target.value)
          }
          className={`w-full px-4 py-2 border rounded mb-2 ${
            !isEditable ? "bg-gray-100" : ""
          }`}
          disabled={!isEditable}
        />
        <div className="grid grid-cols-4 gap-4">
          <input
            placeholder="Country"
            value={formData.address.country}
            onChange={(e) => handleAddressChange("country", e.target.value)}
            className={`px-4 py-2 border rounded ${
              !isEditable ? "bg-gray-100" : ""
            }`}
          />
          <input
            placeholder="State"
            value={formData.address.state}
            onChange={(e) => handleAddressChange("state", e.target.value)}
            className={`px-4 py-2 border rounded ${
              !isEditable ? "bg-gray-100" : ""
            }`}
          />
          <input
            placeholder="City"
            value={formData.address.city}
            onChange={(e) => handleAddressChange("city", e.target.value)}
            className={`px-4 py-2 border rounded ${
              !isEditable ? "bg-gray-100" : ""
            }`}
          />
          <input
            placeholder="Zip Code"
            value={formData.address.zipCode}
            onChange={(e) => handleAddressChange("zipCode", e.target.value)}
            className={`px-4 py-2 border rounded ${
              !isEditable ? "bg-gray-100" : ""
            }`}
            type="number"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block mb-1">Criminal History</label>
        <div className="flex gap-6 mb-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={formData.hasCriminalHistory === true}
              onChange={() => handleChange("hasCriminalHistory", true)}
            />
            Yes
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={formData.hasCriminalHistory === false}
              onChange={() => handleChange("hasCriminalHistory", false)}
            />
            No
          </label>
        </div>
        {formData.hasCriminalHistory === true && (
          <input
            placeholder="If yes, provide description"
            value={formData.criminalHistoryDescription}
            onChange={(e) =>
              handleChange("criminalHistoryDescription", e.target.value)
            }
            disabled={formData.hasCriminalHistory === false}
            className="w-full px-4 py-2 border rounded"
          />
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {isEditable && (
          <button
            type="button"
            onClick={handleSave}
            disabled={(!isChanged || saving) && hasChanges}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              (!isChanged || saving) && hasChanges
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        )}

        {isEditable && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={(!isChanged || saving) && hasChanges}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              (!isChanged || saving) && hasChanges
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <SendHorizonal className="w-4 h-4" />
            )}
            Submit Form
          </button>
        )}
        {hasChanges}
      </div>

      <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8">
        {/* --- Buttons Section using the new component --- */}
        <FormNavigationButtons
          onBack={onBack}
          handleSaveOnly={handleSave}
          handleSaveAndContinue={handleSubmit}
          isEditable={isEditable}
          isChanged={isChanged}
          saving={saving}
          fallbackUsed={fallbackUsed}
          currentStep={currentStep} // Pass current step
          totalSteps={totalSteps} // Pass total steps
        />
        {/* --- End Buttons Section --- */}

        {!isEditable &&
          (user.userRole.id === UserRoles.MANAGER.id ||
            user.userRole.id === UserRoles.SUPER_ADMIN.id) && (
            <ManagerActionButtons
              currentStatusId={company?.applicationStatus?.id || 5}
              onApprove={handleApprove}
              onReject={handleReject}
              onResent={handleResent}
            />
          )}
      </div>
    </section>
  );
}

export default ClientInformationForm;
